import AppIconButton from "../form/AppIconButton";
import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Chip, Paper, Stack, Typography, useTheme } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { AppButton } from "../form";
import OpenSearchIndexPicker from "../OpenSearchIndexPicker";
import { useMaterialUIController } from "../../context";
import { getReportChatShell } from "../../lib/ui/reportChatShell";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";
import { friendlyIndexLabel, resourceChipLabel, type ResourceMeta } from "../../utils/chatResourceLabels";

type Props = {
  selected: string[];
  onChange: (indices: string[]) => void;
  disabled?: boolean;
  iconColor?: string;
  /** Controlled open state; when set, the parent owns opening/closing. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Render only the panel (e.g. when opened from the composer tools palette). */
  hideTrigger?: boolean;
  /** Optional display names / probabilities for selected index ids. */
  labels?: Record<string, ResourceMeta>;
};

export default function ChatIndexPicker({ selected, onChange, disabled, iconColor, labels, open: openProp, onOpenChange, hideTrigger }: Props) {
  const { t } = useTranslation();
  const [controller] = useMaterialUIController();
  const theme = useTheme();
  const custom = useAppCustomStyles();
  const shell = getReportChatShell(theme);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const setOpen = useCallback(
    (next: boolean) => {
      setOpenState(next);
      onOpenChange?.(next);
    },
    [onOpenChange]
  );

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (rootRef.current?.contains(target)) return;
      // Autocomplete listbox/portals render outside this root; keep popover open for selection.
      if (
        target instanceof Element &&
        target.closest(
          ".MuiAutocomplete-popper, .MuiPopover-root, .MuiModal-root, [role='listbox']"
        )
      ) {
        return;
      }
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, setOpen]);

  return (
    <Box ref={rootRef} sx={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
      {!hideTrigger && (
        <AppIconButton
          size="small"
          disabled={disabled}
          busyIgnore
          title={t("chat.tooltips.indices")}
          onClick={() => setOpen(!open)}
          sx={custom.components.chatIndexPicker.triggerButton(iconColor)}
          aria-label={t("chat.indexPicker.open")}
          color={selected.length > 0 || open ? "primary" : "default"}
        >
          <AddIcon fontSize="small" />
        </AppIconButton>
      )}

      {open ? (
        <Paper
          elevation={8}
          sx={{
            ...custom.components.chatIndexPicker.popoverPaper(shell, controller.darkMode),
            position: "absolute",
            bottom: "100%",
            left: 0,
            mb: 1,
            zIndex: (theme) => theme.zIndex.modal,
            width: 360,
            maxWidth: "min(92vw, 360px)",
          }}
        >
          <Typography variant="subtitle2" sx={custom.components.chatIndexPicker.title(shell.assistantText)}>
            {t("chat.indexPicker.title")}
          </Typography>
          <Typography variant="caption" sx={custom.components.chatIndexPicker.hint(shell.muted)}>
            {t("chat.indexPicker.hint")}
          </Typography>

          {selected.length > 0 && (
            <Stack direction="row" flexWrap="wrap" gap={0.5} sx={custom.components.chatIndexPicker.chipsRow}>
              {selected.map((idx) => (
                <Chip
                  key={idx}
                  label={resourceChipLabel(idx, labels?.[idx] || { name: friendlyIndexLabel(idx) })}
                  size="small"
                  title={idx}
                  onDelete={() => onChange(selected.filter((s) => s !== idx))}
                />
              ))}
            </Stack>
          )}

          <OpenSearchIndexPicker
            value={selected}
            onChange={onChange}
            searchEnabled={open}
            disabled={disabled}
            size="small"
            fullWidth
          />

          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={custom.components.chatIndexPicker.actions}>
            <AppButton size="small" variant="outlined" onClick={() => onChange([])}>
              {t("chat.indexPicker.clear")}
            </AppButton>
            <AppButton size="small" variant="contained" onClick={() => setOpen(false)}>
              {t("common.save")}
            </AppButton>
          </Stack>
        </Paper>
      ) : null}
    </Box>
  );
}
